import { camelCase, snakeCase } from 'lodash-es';
import {
  SLOT_CATEGORY_LABELS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
} from 'utils/slot-categories';
import type {
  LtdStyleTimingField,
  LtdStyleTimingParams,
  LtdStyleTimingPrefix,
  LtdTimingParams,
  PrePermitTimingParams,
  PrePermitTimingPrefix,
} from 'types/dps-timing';

/**
 * One LTD-style timing section of the DPS form: LTD, then one per Create New Well status
 * (from the shared category list, so a status added there gets its own section). Each is
 * laid out like LTD's: start offset → permit → spud → completion → first production.
 */
export interface TimingSection {
  /** The slot category the section paces (`ltd`, `pre_permit`, …). */
  category: string;
  /** The camelCase field prefix (`ltd`, `prePermit`, …). */
  prefix: LtdStyleTimingPrefix;
  title: string;
  description: string;
}

/** The four fields of a section, in milestone order. */
export const LTD_STYLE_TIMING_FIELDS: readonly LtdStyleTimingField[] = [
  'StartOffsetMonths',
  'PermitToSpudLagMonths',
  'SpudToCompletionLagMonths',
  'CompletionToFirstProductionLagMonths',
];

/** The lag fields: every field but the start offset (which may be negative). */
export const LTD_STYLE_LAG_FIELDS = LTD_STYLE_TIMING_FIELDS.filter(
  (field) => field !== 'StartOffsetMonths'
);

const LTD_SECTION: TimingSection = {
  category: 'ltd',
  prefix: 'ltd',
  title: 'LTD',
  description: 'Long-Term Development - fully pace-governed',
};

/** The Create New Well statuses' sections, in the shared list's order. */
export const PRE_PERMIT_TIMING_SECTIONS: readonly TimingSection[] =
  WELL_STATUS_SLOT_CATEGORY_KEYS.map((category) => ({
    category,
    prefix: camelCase(category) as PrePermitTimingPrefix,
    title: SLOT_CATEGORY_LABELS[category],
    description: `${SLOT_CATEGORY_LABELS[category]} wells - paced in a train of their own, on these settings`,
  }));

/** Every LTD-style section: LTD's, then the four statuses'. */
export const TIMING_SECTIONS: readonly TimingSection[] = [
  LTD_SECTION,
  ...PRE_PERMIT_TIMING_SECTIONS,
];

/**
 * @param {LtdStyleTimingPrefix} prefix - The section's camelCase prefix.
 * @param {LtdStyleTimingField} field - One of the section's fields.
 * @return {keyof LtdStyleTimingParams} The camelCase key (`prePermitStartOffsetMonths`).
 */
export const timingKey = (
  prefix: LtdStyleTimingPrefix,
  field: LtdStyleTimingField
): keyof LtdStyleTimingParams => `${prefix}${field}`;

/**
 * @param {LtdStyleTimingPrefix} prefix - The section's camelCase prefix.
 * @param {LtdStyleTimingField} field - One of the section's fields.
 * @return {string} The snake_case backend key (`pre_permit_start_offset_months`).
 */
export const timingBackendKey = (
  prefix: LtdStyleTimingPrefix,
  field: LtdStyleTimingField
): string => snakeCase(timingKey(prefix, field));

/**
 * Every Create New Well status's timing, each copied from LTD's values. Seeds a new area
 * (LTD's defaults) and a saved area that predates the per-status columns (its own LTD values),
 * matching the server backfill.
 *
 * @param {LtdTimingParams} ltd - The LTD values to copy.
 * @return {PrePermitTimingParams} The four statuses' fields.
 */
export function prePermitTimingFromLtd(
  ltd: LtdTimingParams
): PrePermitTimingParams {
  return Object.fromEntries(
    PRE_PERMIT_TIMING_SECTIONS.flatMap(({ prefix }) =>
      LTD_STYLE_TIMING_FIELDS.map((field) => [
        timingKey(prefix, field),
        ltd[timingKey('ltd', field) as keyof LtdTimingParams],
      ])
    )
  ) as PrePermitTimingParams;
}

/**
 * The Create New Well statuses' fields picked from a params-shaped object, by camelCase key.
 *
 * @param {Record<string, unknown>} source - Anything carrying the camelCase keys.
 * @return {PrePermitTimingParams} The four statuses' fields.
 */
export function pickPrePermitTiming(
  source: PrePermitTimingParams
): PrePermitTimingParams {
  return Object.fromEntries(
    PRE_PERMIT_TIMING_SECTIONS.flatMap(({ prefix }) =>
      LTD_STYLE_TIMING_FIELDS.map((field) => {
        const key = timingKey(prefix, field) as keyof PrePermitTimingParams;
        return [key, source[key]];
      })
    )
  ) as PrePermitTimingParams;
}
